import { api, type AircraftOverview, type NewAircraftContext } from '../api';
import { basisFor, regimesFor } from './add-aircraft';
import { callout, field, hoursVal, input, opt, openDialog, select, val, numVal } from '../dialog';
import { hoursInputAttrs, hoursValue } from '../hours';
import { html } from '../ui';

/**
 * Correcting an aircraft record.
 *
 * The form is split because the fields are not the same kind of thing. Where an
 * aircraft is based is an operational detail that changes when it moves hangar.
 * Its registration, serial number, owner, regime and counters are what every
 * other record is read against — a compliance means nothing without a known
 * airframe hour reading, a certificate means nothing without a known
 * registration — so changing one of those is a correction, and the reason for
 * it goes on the audit trail with the before and after.
 *
 * The correction half stays closed until it is asked for. Most edits are the
 * top half, and putting the registration next to the base aerodrome invites
 * changing it by accident.
 */

const FIELD_LABEL: Record<string, string> = {
  registration: 'registration',
  registryCountry: 'state of registry',
  serialNumber: 'serial number',
  yearOfManufacture: 'year of manufacture',
  variant: 'variant',
  baseAerodrome: 'base aerodrome',
  customerId: 'owner',
  managedUnderApprovalId: 'managing approval',
  regime: 'regime',
  regimeBasis: 'regime basis',
  regimeJustification: 'regime justification',
  tsnHours: 'airframe hours',
  cyclesTotal: 'cycles',
  landingsTotal: 'landings',
  countersAsOf: 'counters as of',
  lastInspectionAt: 'last inspection',
  lastInspectionTsn: 'hours at the last inspection',
  lastInspectionNote: 'which inspection',
};

export function editAircraft(
  a: AircraftOverview,
  ctx: NewAircraftContext,
  onDone: () => void,
): void {
  let correcting = false;
  // Held here rather than read off the select: the body is rebuilt whenever
  // the regime changes, and the bases below it depend on which one is chosen.
  let regime = a.regime;

  openDialog<Record<string, unknown>>({
    wide: true,
    title: `Edit ${a.registration}`,
    sub: 'Operational details change freely; the record everything else is read against needs a reason',
    primary: 'Save',

    body: () => html`
      <div class="fset">
        <div class="lg">Details</div>
        <div class="frow">
          ${field('Base aerodrome',
            input('baseAerodrome', a.baseAerodrome ?? '', 'style="font-family:var(--mono);text-transform:uppercase" maxlength="8"'))}
          ${field('Year of manufacture',
            input('yearOfManufacture', a.yearOfManufacture ?? '', 'inputmode="numeric"'))}
        </div>
        ${field('Variant', input('variant', a.variant ?? '', 'placeholder="B23-915 IFR" maxlength="80"'),
          'The build standard within the type. Factory service bulletins are issued against it, so the directive check searches on it')}
      </div>

      <div class="fset">
        <div class="lg">Last scheduled inspection</div>
        <div class="frow">
          ${field('Date', input('lastInspectionAt', a.lastInspectionAt?.slice(0, 10) ?? '', 'type="date"'))}
          ${field('At airframe hours',
            input('lastInspectionTsn', hoursValue(a.lastInspectionTsn), hoursInputAttrs()))}
        </div>
        ${field('Which inspection', input('lastInspectionNote', a.lastInspectionNote ?? '', 'placeholder="100-hour / annual, at the Bristell service centre" maxlength="200"'))}
        ${callout('info', 'i', html`
          Stated rather than worked out. An aircraft taken on mid-life has its last inspection in a paper
          logbook, and the nearest thing on file — the newest compliance record — can be a directive
          signed yesterday, which is not an inspection. Reading a programme out of the manuals judges what
          already applies from this date and from the hours, so it is worth being right.
        `)}
      </div>

      <div class="fset">
        <div class="lg">Correct the record</div>
        <label style="display:flex;gap:9px;align-items:flex-start;font-size:13.5px">
          <input type="checkbox" data-correcting ${correcting ? 'checked' : ''} style="margin-top:3px">
          <span>
            I need to correct the <b>registration, serial number, owner, who manages it, its regime
            or its counters</b>.
          </span>
        </label>

        ${correcting
          ? html`
              ${callout('caution', '!', html`
                Every compliance, release and certificate on file is read against these. A correction is
                recorded with what it was, what it became and why — so change them because the record was
                wrong, not to make it say something more convenient.
              `)}

              <div class="frow">
                ${field('Registration',
                  input('registration', a.registration, 'style="font-family:var(--mono);text-transform:uppercase" maxlength="12"'))}
                ${field('State of registry',
                  select('registryCountry',
                    ctx.registries.map((r) => opt(r.code, `${r.country} (${r.prefix}${r.separator})`, r.code === a.registryCountry)).join('')))}
              </div>
              ${field('Serial number', input('serialNumber', a.serialNumber, 'style="font-family:var(--mono)" maxlength="40"'))}

              <div class="frow">
                ${field('Owner',
                  select('customerId', ctx.customers.map((c) => opt(c.id, c.name, c.id === a.customer.id)).join('')))}
                ${field('Contract reference',
                  input('contractReference', a.contract?.reference ?? '', 'maxlength="60"'),
                  'ML.A.201(f) — the written contract this aircraft is managed under')}
              </div>

              <div class="frow">
                ${field('Managed under',
                  select('managedUnderApprovalId', ctx.approvals
                    .map((ap) => opt(ap.id, `${ap.reference} — ${ap.kind}`, ap.id === a.approval?.id))
                    .join('')),
                  'Which of your approvals this aircraft is managed under')}
                ${field('Regime',
                  select('regime', regimesFor(ctx, a.registryCountry)
                    .map((g) => opt(g.value, g.label, g.value === regime))
                    .join(''), 'data-regime'))}
              </div>
              ${basisFor(ctx, regime, a.registryCountry).length
                ? html`
                    <div class="frow">
                      ${field('Why this regime',
                        select('regimeBasis', basisFor(ctx, regime, a.registryCountry)
                          .map((b) => opt(b.value, b.label, regime === a.regime && b.value === a.regimeBasis))
                          .join('')),
                        'The fact the regime follows from, not a preference')}
                      ${field('Justification',
                        input('regimeJustification', a.regimeJustification ?? '', 'maxlength="400"'))}
                    </div>
                  `
                : ''}
              ${callout('caution', '!', html`
                Moving an aircraft between approvals or regimes changes which release bases it may be
                released under and which airworthiness review outcomes are available. A Part-CAO may
                not manage an aircraft on a licensed air carrier's AOC, and saving that combination
                is refused rather than recorded.
              `)}

              <div class="frow3">
                ${field('Airframe hours', input('tsnHours', hoursValue(a.tsnHours), hoursInputAttrs()))}
                ${field('Landings', input('landingsTotal', a.landingsTotal, 'inputmode="numeric" style="text-align:right;font-family:var(--mono)"'))}
                ${field('Cycles', input('cyclesTotal', a.cyclesTotal, 'inputmode="numeric" style="text-align:right;font-family:var(--mono)"'))}
              </div>
              ${callout('info', 'i', html`
                Moving the counters moves every due date measured against them, so the forecast is
                recomputed. Correcting a single mis-typed flight is better done by amending that flight in
                the tech log, which keeps the error and its correction side by side.
              `)}

              ${field('Reason for the correction',
                input('reason', '', 'placeholder="Opening counters transposed at hand-over; logbook reads 1350.0 h" maxlength="400"'),
                'Recorded with the before and after')}
            `
          : ''}
      </div>
    `,

    onRender: (root, refresh) => {
      root.querySelector<HTMLInputElement>('[data-correcting]')?.addEventListener('change', (e) => {
        correcting = (e.target as HTMLInputElement).checked;
        refresh();
      });
      // The reasons on offer depend on the regime, so the body is redrawn.
      root.querySelector<HTMLSelectElement>('[data-regime]')?.addEventListener('change', (e) => {
        regime = (e.target as HTMLSelectElement).value;
        refresh();
      });
    },

    collect: (root) => {
      const payload: Record<string, unknown> = {
        baseAerodrome: val(root, 'baseAerodrome').trim().toUpperCase() || undefined,
        yearOfManufacture: numVal(root, 'yearOfManufacture'),
        variant: val(root, 'variant').trim() || undefined,
        // null rather than undefined: emptying the field has to clear the
        // date, not quietly leave the old one in place.
        lastInspectionAt: val(root, 'lastInspectionAt') || null,
        lastInspectionTsn: hoursVal(root, 'lastInspectionTsn') ?? null,
        lastInspectionNote: val(root, 'lastInspectionNote').trim() || null,
      };

      if (correcting) {
        const reason = val(root, 'reason').trim();
        const registration = val(root, 'registration').trim().toUpperCase();
        if (!registration) return { error: 'A registration is required.' };
        if (!val(root, 'serialNumber').trim()) return { error: 'A serial number is required.' };

        Object.assign(payload, {
          registration,
          registryCountry: val(root, 'registryCountry'),
          serialNumber: val(root, 'serialNumber').trim(),
          customerId: val(root, 'customerId'),
          contractReference: val(root, 'contractReference').trim() || undefined,
          managedUnderApprovalId: val(root, 'managedUnderApprovalId') || undefined,
          regime: val(root, 'regime') || undefined,
          regimeBasis: val(root, 'regimeBasis') || undefined,
          regimeJustification: val(root, 'regimeJustification').trim() || undefined,
          tsnHours: hoursVal(root, 'tsnHours'),
          landingsTotal: numVal(root, 'landingsTotal'),
          cyclesTotal: numVal(root, 'cyclesTotal'),
          reason: reason || undefined,
        });
      }

      return payload;
    },

    submit: (payload) => api.aircraft.update(a.id, payload),
    done: (r) =>
      r.unchanged
        ? 'Nothing changed'
        : `${r.registration} updated — ${r.changed.map((f: string) => FIELD_LABEL[f] ?? f).join(', ')}${
            r.forecastRecomputed ? ' · forecast recomputed' : ''
          }`,
    onDone,
  });
}
